import {
  EXTERNAL_LOAN_INTEGRATION_TEST_DAILY_INTEREST_RATE_PERCENT,
  EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE,
} from '../constants/externalLoanConstants';
import {
  calculateCollateralCoverage,
  createMockApplicationSnapshot,
  createMockSubmittedExternalLoanApplication,
  externalLoanApplicantProfileFixtures,
  externalLoanApplicationDraftFixture,
  externalLoanCollateralCoverageFixtures,
  externalLoanMockApplications,
  externalLoanSimulationStates,
} from './externalLoanMockData';

const DEFAULT_DATA_SOURCE = 'mock';
const LEADER_ROLE_MAP = {
  mwenyekiti: 'Chairperson',
  chairperson: 'Chairperson',
  katibu: 'Secretary',
  secretary: 'Secretary',
  muhasibu: 'Accountant',
  mhasibu: 'Accountant',
  accountant: 'Accountant',
  treasurer: 'Accountant',
};
const REQUIRED_GUARANTOR_ROLES = ['Chairperson', 'Secretary', 'Accountant'];

const money = (value) => Math.max(0, Math.round(Number(value || 0)));
const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
const normalizeRoleKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const normalizeExternalLoanIdentityType = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === 'NIDA' || normalized === 'NATIONAL_ID' || normalized === 'NATIONAL ID') return 'National ID';
  if (normalized === 'VOTER' || normalized === 'VOTER_ID' || normalized === 'VOTER ID') return 'Voter ID';
  return value || '';
};

export const calculateExternalLoanEligibility = (
  currentSavings,
  protectedSavingsPercentage = EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE,
) => {
  const safeSavings = money(currentSavings);
  const safePercentage = Number.isFinite(Number(protectedSavingsPercentage))
    ? Number(protectedSavingsPercentage)
    : EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE;
  const protectedAmount = money(safeSavings * safePercentage);
  return {
    currentSavings: safeSavings,
    protectedSavingsPercentage: safePercentage,
    protectedAmount,
    eligibleAmount: Math.max(safeSavings - protectedAmount, 0),
  };
};

export const buildExternalLoanCollateralCoverage = (eligibility, requestedAmount = 0) => {
  const coverageSource = {
    key: 'real-savings',
    label: 'Current member savings',
    currentSavings: eligibility?.currentSavings || 0,
    protectedSavings: eligibility?.protectedAmount || 0,
  };
  return calculateCollateralCoverage(coverageSource, requestedAmount);
};

export const buildExternalLoanApplicantProfile = ({ authUser = {}, userProfile = {}, activeMembership = {}, memberDetails = {} } = {}) => {
  const memberUser = memberDetails.user || {};
  return {
    key: 'real-member-profile',
    label: 'Current member profile',
    fullName: pickFirst(userProfile.full_name, memberUser.full_name, authUser.full_name),
    phoneNumber: pickFirst(userProfile.phone_number, memberUser.phone_number, authUser.phone_number),
    governmentIdType: normalizeExternalLoanIdentityType(memberDetails.identity_type),
    governmentIdNumber: memberDetails.identity_number || '',
    groupName: pickFirst(activeMembership.group_name, memberDetails.group?.name, authUser.group_name),
    memberReference: pickFirst(
      activeMembership.membership_no,
      activeMembership.payment_reference,
      memberDetails.payment_reference,
      memberDetails.membership_no,
      authUser.payment_reference,
    ),
  };
};

export const buildExternalLoanGuarantors = (members = []) => REQUIRED_GUARANTOR_ROLES.map((role) => {
  const match = members.find((member) => {
    const normalizedRole = LEADER_ROLE_MAP[normalizeRoleKey(member.role)];
    return normalizedRole === role && member.is_active !== false;
  });
  const user = match?.user || {};
  const name = pickFirst(user.full_name, match?.full_name);
  const phoneNumber = pickFirst(user.phone_number, match?.phone_number);
  return {
    role,
    name: name || '',
    phoneNumber: phoneNumber || '',
    verificationStatus: match ? 'Verified active group leader' : `${role} has not been assigned for this group.`,
    acknowledgementStatus: match ? 'Ready for approval after submission' : 'Missing required group leader',
    missing: !match,
  };
});

export const selectBellicksIntegrationRate = (
  rates = [],
  repaymentTerm,
) => {
  const term = Number(repaymentTerm);
  if (!Number.isInteger(term) || term < 1) return null;

  return rates.find((rate) => {
    const frequency = String(rate?.repayment_frequency || '').trim().toUpperCase();
    const value = Number(rate?.rate);
    const termMin = rate?.term_min == null ? null : Number(rate.term_min);
    const termMax = rate?.term_max == null ? null : Number(rate.term_max);
    return rate?.is_active !== false
      && frequency === 'DAILY'
      && value === EXTERNAL_LOAN_INTEGRATION_TEST_DAILY_INTEREST_RATE_PERCENT
      && (termMin == null || term >= termMin)
      && (termMax == null || term <= termMax);
  }) || null;
};

export const buildExternalLoanApplicationContext = ({
  authUser = {},
  userProfile = {},
  memberships = [],
  memberDetails = {},
  groupMembers = [],
  protectedSavingsPercentage = EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE,
  providerInterestRates = [],
  integrationMode = false,
} = {}) => {
  const activeMembership =
    memberships.find((membership) => membership.is_current) ||
    memberships.find((membership) => String(membership.id) === String(authUser.member_id)) ||
    (memberships.length === 1 ? memberships[0] : null);
  if (!activeMembership) {
    return {
      ready: false,
      reason: 'No active group membership was found.',
    };
  }

  const currentSavings = pickFirst(activeMembership.total_savings, memberDetails.balance, activeMembership.live_savings, 0);
  const eligibility = calculateExternalLoanEligibility(currentSavings, protectedSavingsPercentage);
  const applicantProfile = buildExternalLoanApplicantProfile({ authUser, userProfile, activeMembership, memberDetails });
  const guarantors = buildExternalLoanGuarantors(groupMembers);
  const missing = [];
  if (!applicantProfile.fullName) missing.push('full name');
  if (!applicantProfile.phoneNumber) missing.push('phone number');
  if (!applicantProfile.governmentIdType || !applicantProfile.governmentIdNumber) missing.push('government identification');
  if (!applicantProfile.groupName) missing.push('active group');
  if (!applicantProfile.memberReference) missing.push('member reference/control number');

  return {
    ready: missing.length === 0,
    reason: missing.length ? `Missing required member data: ${missing.join(', ')}.` : null,
    activeMembership,
    applicantProfile,
    eligibility,
    collateralCoverage: buildExternalLoanCollateralCoverage(eligibility, 0),
    guarantors,
    providerInterestRates: integrationMode ? providerInterestRates : [],
    integrationMode: Boolean(integrationMode),
  };
};

export const getExternalLoanDataSource = (options = {}) => {
  const value = (options.env?.REACT_APP_EXTERNAL_LOANS_DATA_SOURCE || options.env?.EXTERNAL_LOANS_DATA_SOURCE || '').toString().trim().toLowerCase();
  if (value === 'api') {
    return 'api';
  }
  return DEFAULT_DATA_SOURCE;
};
export const isBellicksIntegrationTestRateEnabled = (options = {}) => (
  options.dataSource === 'api'
  && String(
    options.env?.REACT_APP_EXTERNAL_LOAN_INTEGRATION_TEST_RATE_ENABLED || '',
  ).trim().toLowerCase() === 'true'
);


export const getSimulationStateConfig = (stateKey) => {
  const state = externalLoanSimulationStates.find((item) => item.key === stateKey);
  return state || externalLoanSimulationStates[0];
};



const API_STATUS_STATE_MAP = {
  PENDING_CHAIR_APPROVAL: 'awaiting-chairperson',
  PENDING_SECRETARY_APPROVAL: 'awaiting-secretary',
  PENDING_ACCOUNTANT_APPROVAL: 'awaiting-accountant',
  PROVIDER_SUBMISSION_PENDING: 'provider-sync-pending',
  SUBMITTING_TO_PROVIDER: 'provider-sync-pending',
  PROVIDER_SUBMISSION_FAILED: 'provider-sync-failed',
  SUBMITTED_TO_PROVIDER: 'bellicks-submitted',
  REJECTED: 'rejected',
};

const PROVIDER_STATUS_STATE_MAP = {
  GM_REVIEW: 'gm-review',
  UNDER_REVIEW: 'gm-review',
  IN_REVIEW: 'gm-review',
  APPROVED: 'approved',
  DISBURSED: 'disbursed',
  REPAYMENT: 'partially-repaid',
  PARTIALLY_REPAID: 'partially-repaid',
  OVERDUE: 'overdue',
  COMPLETED: 'fully-repaid',
  FULLY_REPAID: 'fully-repaid',
  REJECTED: 'rejected',
};

const API_STATE_PRESENTATION = {
  'awaiting-chairperson': ['Awaiting Chairperson Approval', 'Chairperson Approval Pending'],
  'awaiting-secretary': ['Awaiting Secretary Approval', 'Secretary Approval Pending'],
  'awaiting-accountant': ['Awaiting Accountant Approval', 'Accountant Approval Pending'],
  'provider-sync-pending': ['Submitting to Bellicks', 'Automatic Provider Submission'],
  'provider-sync-failed': ['Provider Sync Needs Attention', 'Bellicks Submission Failed'],
  'bellicks-submitted': ['Submitted to Bellicks', 'Submitted to Bellicks'],
  'gm-review': ['Under Bellicks Review', 'GM Review'],
  approved: ['Approved', 'Approved by Bellicks'],
  disbursed: ['Disbursed', 'Disbursed by Finance'],
  'partially-repaid': ['Repayment Active', 'Repayment'],
  overdue: ['Overdue', 'Repayment Overdue'],
  'fully-repaid': ['Completed', 'Completed'],
  rejected: ['Rejected', 'Decision Issued'],
};

export const getExternalLoanApiStateKey = (application = {}) => {
  const providerStatus = String(application.provider_status || '').trim().toUpperCase();
  if (application.status === 'SUBMITTED_TO_PROVIDER' && PROVIDER_STATUS_STATE_MAP[providerStatus]) {
    return PROVIDER_STATUS_STATE_MAP[providerStatus];
  }
  return API_STATUS_STATE_MAP[application.status] || 'submitted';
};

export const formatExternalLoanTerm = (loanSummary = {}) => {
  const term = Number(loanSummary.repaymentTerm ?? loanSummary.termMonths ?? 0);
  const frequency = String(loanSummary.repaymentFrequency || 'MONTHLY').trim().toUpperCase();
  const unit = frequency === 'DAILY'
    ? (term === 1 ? 'day' : 'days')
    : frequency === 'WEEKLY'
      ? (term === 1 ? 'week' : 'weeks')
      : (term === 1 ? 'month' : 'months');
  return `${term} ${unit}`;
};

export const applyExternalLoanApiLifecycle = (snapshot = {}, application = {}) => {
  const stateKey = getExternalLoanApiStateKey(application);
  const [status, currentStage] = API_STATE_PRESENTATION[stateKey] || ['Submitted', 'System Eligibility Review'];
  const referenceNumber = application.external_reference || snapshot.referenceNumber;
  const approvedAtByRole = {
    Chairperson: application.chair_approved_at,
    Secretary: application.secretary_approved_at,
    Accountant: application.accountant_approved_at,
  };
  const baseGuarantors = snapshot.guarantors?.length
    ? snapshot.guarantors
    : REQUIRED_GUARANTOR_ROLES.map((role) => ({ role, name: '', phone: '' }));
  const guarantors = baseGuarantors.map((guarantor) => {
    const approvedAt = approvedAtByRole[guarantor.role];
    return {
      ...guarantor,
      status: approvedAt ? 'approved' : 'pending',
      approved: Boolean(approvedAt),
      approvedAt: approvedAt || null,
    };
  });
  const repaymentTerm = Number(
    application.repayment_term
    ?? snapshot.loanSummary?.repaymentTerm
    ?? snapshot.loanSummary?.termMonths
    ?? 0
  );
  const repaymentFrequency = application.repayment_frequency
    || snapshot.loanSummary?.repaymentFrequency;

  return {
    ...snapshot,
    id: referenceNumber ? `api-${referenceNumber}` : snapshot.id,
    referenceNumber,
    stateKey,
    stateConfig: getSimulationStateConfig(stateKey),
    status,
    currentStage,
    statusGroup: ['approved'].includes(stateKey)
      ? 'approved'
      : ['disbursed', 'partially-repaid'].includes(stateKey)
        ? 'active'
        : stateKey === 'fully-repaid'
          ? 'completed'
          : stateKey === 'rejected'
            ? 'rejected'
            : 'pending',
    applicant: application.applicant_name || snapshot.applicant,
    amount: Number(application.amount ?? snapshot.amount ?? 0),
    lastUpdated: application.updated_at || snapshot.lastUpdated,
    memberProfile: {
      ...(snapshot.memberProfile || {}),
      memberName: application.applicant_name || snapshot.memberProfile?.memberName,
      phoneNumber: application.applicant_phone_number || snapshot.memberProfile?.phoneNumber,
      group: application.applicant_group_name || snapshot.memberProfile?.group,
      governmentIdType: application.government_id_type || snapshot.memberProfile?.governmentIdType,
      memberReference: application.owner_control_number || snapshot.memberProfile?.memberReference,
    },
    groupProfile: {
      ...(snapshot.groupProfile || {}),
      name: application.applicant_group_name || snapshot.groupProfile?.name,
    },
    eligibility: {
      ...(snapshot.eligibility || {}),
      currentSavings: Number(application.current_savings ?? snapshot.eligibility?.currentSavings ?? 0),
      protectedAmount: Number(application.protected_savings_amount ?? snapshot.eligibility?.protectedAmount ?? 0),
      eligibleAmount: Number(application.pledged_savings_value ?? snapshot.eligibility?.eligibleAmount ?? 0),
    },
    loanSummary: {
      ...(snapshot.loanSummary || {}),
      requestedAmount: Number(application.amount ?? snapshot.loanSummary?.requestedAmount ?? 0),
      approvedAmount: Number(application.amount ?? snapshot.loanSummary?.approvedAmount ?? 0),
      dailyInterestRate: Number(
        application.daily_interest_rate ?? snapshot.loanSummary?.dailyInterestRate ?? 0
      ),
      interest: Number(application.interest_amount ?? snapshot.loanSummary?.interest ?? 0),
      fees: 0,
      totalPayable: Number(application.total_payable ?? snapshot.loanSummary?.totalPayable ?? 0),
      repaymentTerm,
      repaymentFrequency,
      termMonths: String(repaymentFrequency || '').trim().toUpperCase() === 'MONTHLY'
        ? repaymentTerm
        : undefined,
    },
    collateral: {
      type: application.collateral_type || snapshot.collateral?.type || 'SAVINGS',
      description: 'Eligible Savvyy member savings pledged as security.',
      estimatedValue: Number(application.pledged_savings_value ?? snapshot.collateral?.estimatedValue ?? 0),
    },
    guarantors,
    contract: {
      ...(snapshot.contract || {}),
      reference: application.external_reference || snapshot.contract?.reference,
      status: application.contract_generated_at ? 'Generated' : 'Not Generated',
      generatedAt: application.contract_generated_at || snapshot.contract?.generatedAt,
      canSign: false,
    },
    bellicksReview: {
      ...(snapshot.bellicksReview || {}),
      reference: application.provider_external_reference || snapshot.bellicksReview?.reference,
      status: application.provider_status || status,
      stage: currentStage,
      submissionDate: application.provider_submitted_at || snapshot.bellicksReview?.submissionDate,
      notes: application.provider_submission_error || snapshot.bellicksReview?.notes,
    },
    providerSyncError: application.provider_sync_error || (
      application.provider_submission_error
        ? {
          detail: application.provider_submission_error,
          code: application.provider_submission_error_code,
          retryable: application.provider_submission_retryable,
        }
        : null
    ),
  };
};

export const buildMockApplicationSnapshot = (stateKey = 'eligible') => {
  const stateConfig = getSimulationStateConfig(stateKey);
  return createMockApplicationSnapshot(stateConfig);
};

export const getMockExternalLoanApplications = () => externalLoanMockApplications;

export const getMockExternalLoanApplicationDraft = () => JSON.parse(JSON.stringify(externalLoanApplicationDraftFixture));

export const getMockExternalLoanApplicantProfiles = () => JSON.parse(JSON.stringify(externalLoanApplicantProfileFixtures));

export const getMockExternalLoanCollateralCoverageScenarios = () => JSON.parse(JSON.stringify(externalLoanCollateralCoverageFixtures));

export const buildMockCollateralCoverage = (coverageSource, requestedAmount) => calculateCollateralCoverage(coverageSource, requestedAmount);

export const buildMockSubmittedExternalLoanApplication = (draft, options = {}) => createMockSubmittedExternalLoanApplication(draft, options);

export const calculateMockEligibility = (currentSavings, protectedSavingsPercentage = EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE) => {
  const safeSavings = Number(currentSavings || 0);
  const safePercentage = Number(protectedSavingsPercentage || EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE);
  const protectedAmount = safeSavings * safePercentage;
  return {
    currentSavings: safeSavings,
    protectedAmount,
    protectedSavingsPercentage: safePercentage,
    eligibleAmount: safeSavings - protectedAmount,
  };
};
